import { Save, Loader2, ShieldAlert, Lock, Unlock, KeyRound, Trash2, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export function ClientAccessTab({
  clientAccess, setClientAccess, toggleAccess, saveClientAccess, savingAccess,
  data, handleToggleBlock, blockingAccount,
  showResetPw, setShowResetPw, newPassword, setNewPassword,
  handleResetPassword, resettingPw,
  setShowDeleteConfirm,
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Access Control */}
      <div className="space-y-4">
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-1" style={{ color: "#f7c935" }}>Acces du client</h3>
          <p className="text-xs text-slate-500 mb-4">Cochez les modules visibles pour ce client.</p>
          <div className="space-y-5">
            {[
              { title: null, items: [
                { key: "messages", label: "Messages" },
                { key: "agenda", label: "Agenda" },
              ]},
              { title: "Prestations Blitzz", items: [
                { key: "audits", label: "Audits" },
                { key: "accompagnement", label: "Accompagnements" },
                { key: "services", label: "Services" },
                { key: "documents", label: "Documents" },
                { key: "club", label: "Club" },
                { key: "expert_strategie", label: "Expert Strategie IA" },
              ]},
              { title: "Formation", items: [
                { key: "formations", label: "Formations" },
              ]},
              { title: "Comptabilite Blitzz", items: [
                { key: "social", label: "Social" },
              ]},
              { title: "Gestion activite", items: [
                { key: "documents_gestion", label: "Documents" },
                { key: "fiche", label: "Ma Fiche" },
              ]},
            ].map(group => {
              const allOn = group.items.every(i => clientAccess[i.key]);
              const toggleGroup = () => {
                const newVal = !allOn;
                const updated = { ...clientAccess };
                group.items.forEach(i => { updated[i.key] = newVal; });
                setClientAccess(updated);
              };
              return (
                <div key={group.title || "base"}>
                  {group.title && (
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{group.title}</div>
                      <button onClick={toggleGroup} data-testid={`group-toggle-${(group.title || "").replace(/[^a-z]/gi, "")}`}
                        className={`text-[9px] px-2 py-0.5 rounded-full font-medium transition-colors ${allOn ? "bg-[#f7c935]/20 text-[#f7c935]" : "bg-white/5 text-slate-500 hover:bg-white/10"}`}>
                        {allOn ? "Tout desactiver" : "Tout activer"}
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {group.items.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer" data-testid={`access-toggle-${key}`}>
                        <div className={`w-10 h-5 rounded-full transition-colors relative ${clientAccess[key] ? "bg-[#f7c935]" : "bg-white/10"}`}
                          onClick={() => toggleAccess(key)}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${clientAccess[key] ? "translate-x-5" : "translate-x-0.5"}`} />
                        </div>
                        <span className={`text-sm ${clientAccess[key] ? "text-white" : "text-slate-500"}`}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <Button onClick={saveClientAccess} disabled={savingAccess} className="mt-4 bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold w-full" data-testid="save-access-btn">
            {savingAccess ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer les acces
          </Button>
        </div>
      </div>

      {/* Account Management */}
      <div className="space-y-4">
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-400" /> Gestion du compte</h3>
          <div className="space-y-3">
            {/* Block/Unblock */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#0b0811] border border-white/5">
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  {data?.user?.is_blocked ? <Lock className="w-4 h-4 text-red-400" /> : <Unlock className="w-4 h-4 text-green-400" />}
                  {data?.user?.is_blocked ? "Compte bloque" : "Compte actif"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {data?.user?.is_blocked ? "Le client ne peut pas se connecter" : "Le client a acces a la plateforme"}
                </div>
              </div>
              <Button onClick={handleToggleBlock} disabled={blockingAccount}
                variant="outline" size="sm"
                className={`text-xs font-bold ${data?.user?.is_blocked ? "border-green-500/30 text-green-400 hover:bg-green-500/10" : "border-red-500/30 text-red-400 hover:bg-red-500/10"}`}
                data-testid="toggle-block-btn">
                {blockingAccount ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {data?.user?.is_blocked ? "Debloquer" : "Bloquer"}
              </Button>
            </div>

            {/* Reset Password */}
            <div className="p-3 rounded-lg bg-[#0b0811] border border-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-[#f7c935]" /> Mot de passe
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Reinitialiser le mot de passe du client</div>
                </div>
                <Button onClick={() => setShowResetPw(!showResetPw)} variant="outline" size="sm"
                  className="text-xs font-bold border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10"
                  data-testid="show-reset-pw-btn">
                  Reinitialiser
                </Button>
              </div>
              {showResetPw && (
                <div className="mt-3 flex gap-2">
                  <Input type="password" placeholder="Nouveau mot de passe (min. 6 car.)"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    className="bg-[#161020] border-white/10 text-white text-sm flex-1"
                    data-testid="new-password-input" />
                  <Button onClick={handleResetPassword} disabled={resettingPw || newPassword.length < 6}
                    className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold text-xs" data-testid="confirm-reset-pw-btn">
                    {resettingPw ? <Loader2 className="w-3 h-3 animate-spin" /> : "Valider"}
                  </Button>
                </div>
              )}
            </div>

            {/* Delete account */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/5 border border-red-500/10">
              <div>
                <div className="text-sm font-medium text-red-400">Supprimer le compte</div>
                <div className="text-xs text-slate-500 mt-0.5">Action irreversible</div>
              </div>
              <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" size="sm"
                className="text-xs font-bold border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-client-btn">
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
